import type {StudyPlan,StudySession,StudyDraft,StudyRevision} from '../../domain/study/model';
import type {SopTemplate,SopVersion,SopRun,SopDraft,SopRevision} from '../../domain/sop/model';
import type {SearchDocument} from '../../application/search-index';
import type {Content,ContentDraft,ContentRevision} from '../../domain/content/model';
import type {Task,Event,Project,ItemRevision,Relation,Directive,ItemDraft} from '../../domain/items/model';
import Dexie, { type Table } from 'dexie';
import type { Attachment, AttachmentBlob, Capture, CaptureRevision, Control, Draft, Meta, Operation, Setting } from '../../domain/capture/model';
import { NookError } from '../../domain/shared/errors';

export const DATA_SCHEMA_VERSION = 6;
export class NookDatabase extends Dexie {
  manualStudyPlans!:Table<StudyPlan,string>;manualStudySessions!:Table<StudySession,string>;manualStudyDrafts!:Table<StudyDraft,string>;
  sopTemplates!:Table<SopTemplate,string>;sopTemplateVersions!:Table<SopVersion,string>;sopRuns!:Table<SopRun,string>;sopDrafts!:Table<SopDraft,string>;
  captures!: Table<Capture, string>;
  drafts!: Table<Draft, string>;
  attachments!: Table<Attachment, string>;
  attachmentBlobs!: Table<AttachmentBlob, string>;
  operations!: Table<Operation, string>;
  meta!: Table<Meta, string>;
  settings!: Table<Setting, string>;
  revisions!: Table<CaptureRevision | ItemRevision | ContentRevision | SopRevision | StudyRevision, string>;
  tasks!:Table<Task,string>;events!:Table<Event,string>;projects!:Table<Project,string>;relations!:Table<Relation,string>;homeDirectives!:Table<Directive,string>;itemDrafts!:Table<ItemDraft,string>;
  searchDocuments!:Table<SearchDocument,string>;
  resources!:Table<Content,string>;decisions!:Table<Content,string>;diaryEntries!:Table<Content,string>;contentDrafts!:Table<ContentDraft,string>;tags!:Table<{id:string;name:string;normalized:string},string>;
  constructor(name: string) {
    super(name);
    this.version(1).stores({
      captures: 'id, updatedAt, state', drafts: 'id, captureId, updatedAt, state',
      attachments: 'id, sha256', attachmentBlobs: 'sha256', operations: 'id, committedAt',
      meta: 'key', settings: 'key',
    });
    this.version(2).stores({ revisions: 'id, captureId, [captureId+revision]' }).upgrade(async (tx) => {
      const captures = await tx.table('captures').toArray() as Capture[];
      await tx.table('revisions').bulkPut(captures.map((snapshot) => ({ id: `${snapshot.id}:${snapshot.revision}`, captureId: snapshot.id, revision: snapshot.revision, reason: 'migration-baseline', recordedAt: snapshot.updatedAt, snapshot })));
      await tx.table('meta').put({ key: 'schemaVersion', value: 2 });
    });
    this.version(3).stores({tasks:'id, state, plannedDate, reviewDate, projectId',events:'id, state, startAt, projectId',projects:'id, state, updatedAt',relations:'id, captureId, [targetType+targetId]',homeDirectives:'id',itemDrafts:'id, targetId, updatedAt',revisions:'id, captureId, [captureId+revision], [entityType+entityId]'}).upgrade(async tx=>{await tx.table('meta').put({key:'schemaVersion',value:3});});
    this.version(4).stores({searchDocuments:'id, entityType, entityId, *grams',resources:'id, updatedAt, *tagIds',decisions:'id, updatedAt, *tagIds',diaryEntries:'id, diaryDate, updatedAt, *tagIds',contentDrafts:'id, targetId, updatedAt',tags:'id, &normalized'}).upgrade(async tx=>{await tx.table('meta').put({key:'schemaVersion',value:4});});
    this.version(5).stores({sopTemplates:'id, currentVersionId, updatedAt',sopTemplateVersions:'id, templateId, &[templateId+version]',sopRuns:'id, templateId, templateVersionId, state, updatedAt',sopDrafts:'id, targetId, updatedAt'}).upgrade(async tx=>{await tx.table('meta').put({key:'schemaVersion',value:5});});
    this.version(6).stores({manualStudyPlans:'id, state, updatedAt',manualStudySessions:'id, [planId+studyDate], studyDate, state',manualStudyDrafts:'id, targetId, updatedAt'}).upgrade(async tx=>{await tx.table('meta').put({key:'schemaVersion',value:6});});
    this.on('versionchange', () => this.close());
  }
}

export class ControlDatabase extends Dexie {
  entries!: Table<Control, string>;
  constructor(name = 'nook-control') { super(name); this.version(1).stores({ entries: 'key' }); }
}

export async function assertWritable(db: NookDatabase): Promise<void> {
  if ((await db.meta.get('writeState'))?.value !== 'active') {
    throw new NookError('maintenance', '本机数据正在维护或已切换版本，尚未保存。请保留输入后重新打开 Nook。');
  }
}

export async function openPersonalDatabase(): Promise<NookDatabase> {
  const control = new ControlDatabase();
  await control.open();
  let pointer = await control.entries.get('activeDatabase');
  if (!pointer) {
    // Never silently replace an orphaned personal database after losing its pointer.
    const names = await Dexie.getDatabaseNames();
    // Another tab may have installed the pointer while names were being enumerated.
    pointer = await control.entries.get('activeDatabase');
    if (!pointer && names.some((name) => name.startsWith('nook-personal-'))) {
      control.close();
      throw new NookError('generation', '发现已有本机数据库，但当前库指针缺失。原数据未删除，请保留此浏览器环境并联系维护者检查。');
    }
    const name = `nook-personal-${crypto.randomUUID()}`;
    // The transaction elects one initializer even when two fresh tabs open together.
    pointer = await control.transaction('rw', control.entries, async () => {
      const existing = await control.entries.get('activeDatabase');
      if (existing) return existing;
      await control.entries.bulkPut([{ key: 'activeDatabase', value: name }, { key: 'initializing', value: name }]);
      return { key: 'activeDatabase', value: name };
    });
  }
  const initializing = (await control.entries.get('initializing'))?.value === pointer.value;
  if (!initializing && !(await Dexie.exists(pointer.value))) {
    control.close();
    throw new NookError('generation', '当前本机数据库无法找到。没有建立空库或覆盖旧数据，请先检查浏览器存储环境。');
  }
  const db = new NookDatabase(pointer.value);
  await db.open();
  if (initializing) {
    await db.transaction('rw', db.meta, db.settings, async () => {
      if (!(await db.meta.get('datasetId'))) {
        await db.meta.bulkPut([
          { key: 'datasetId', value: crypto.randomUUID() },
          { key: 'writeState', value: 'active' },
          { key: 'schemaVersion', value: DATA_SCHEMA_VERSION },
        ]);
        await db.settings.put({ key: 'timeZone', value: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' });
      }
    });
    await control.entries.delete('initializing');
  }
  if (!(await db.meta.get('datasetId'))) {
    db.close(); control.close();
    throw new NookError('generation', '数据库信息不完整。原库已保留，未执行清空或重建。');
  }
  control.close();
  return db;
}
/** Durable retry marker committed with domain writes, excluded from portable facts. */
export async function markSearchDirty(db:NookDatabase){await db.meta.put({key:'searchDirty',value:1});}
